import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  title: string;
  message?: React.ReactNode;
  type: ToastType;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (
    title: string,
    message?: React.ReactNode,
    type?: ToastType,
    duration?: number
  ) => void;
  removeToast: (id: string) => void;
  pauseToast: (id: string) => void;
  resumeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  showToast: () => {
    // Default no-op implementation, overridden by ToastProvider
  },
  removeToast: () => {
    // Default no-op implementation, overridden by ToastProvider
  },
  pauseToast: () => {
    // Default no-op implementation, overridden by ToastProvider
  },
  resumeToast: () => {
    // Default no-op implementation, overridden by ToastProvider
  },
});

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const startTimesRef = useRef<Map<string, number>>(new Map());
  const remainingTimesRef = useRef<Map<string, number>>(new Map());

  const showToast = useCallback(
    (
      title: string,
      message?: React.ReactNode,
      type: ToastType = 'info',
      duration = 5000
    ) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, title, message, type };

      setToasts(prev => [...prev, newToast]);

      startTimesRef.current.set(id, Date.now());
      remainingTimesRef.current.set(id, duration);

      // Auto-remove after duration
      const timeout = setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
        timeoutsRef.current.delete(id);
        startTimesRef.current.delete(id);
        remainingTimesRef.current.delete(id);
      }, duration);

      timeoutsRef.current.set(id, timeout);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
    startTimesRef.current.delete(id);
    remainingTimesRef.current.delete(id);
  }, []);

  const pauseToast = useCallback((id: string) => {
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);

      // Calculate remaining time
      const startTime = startTimesRef.current.get(id);
      const originalDuration = remainingTimesRef.current.get(id);
      if (startTime && originalDuration) {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, originalDuration - elapsed);
        remainingTimesRef.current.set(id, remaining);
      }
    }
  }, []);

  const resumeToast = useCallback((id: string) => {
    const remaining = remainingTimesRef.current.get(id);
    if (remaining !== undefined) {
      startTimesRef.current.set(id, Date.now());

      const timeout = setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
        timeoutsRef.current.delete(id);
        startTimesRef.current.delete(id);
        remainingTimesRef.current.delete(id);
      }, remaining);

      timeoutsRef.current.set(id, timeout);
    }
  }, []);

  return (
    <ToastContext.Provider
      value={{ toasts, showToast, removeToast, pauseToast, resumeToast }}
    >
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
